import React, { FC, useEffect } from 'react';
import EquityTable from './components/table/table.component';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import ExposureGraphics from './components/ExposureGraphics.component';
import { useDispatch } from 'react-redux';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useAssetClassReportPageModel } from '../../../../api/rs-hg/asset-class/hooks/queries';
import { EquityServerData, isEquityServerData } from './components/table/table.data';

export const assetClassUrls = {
  FETCH_EQUITY_DATA_FIRST_HALF: 'most_recently_stored_asset_class_report',
  FETCH_EQUITY_DATA_SECOND_HALF: 'equity_specific_data',
  FETCH_FIXED_INCOME_FIRST_HALF: 'most_recently_stored_asset_class_report',
  FETCH_FIXED_INCOM_SECOND_HALF: 'bond_specific_data',
};

const Equity: FC<FundInfoComponentProps> = ({ fundId, positionDate }) => {
  const dispatch = useDispatch();

  // Add the select components to the title bar
  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Visualisation: ',
        titleBarKey: 'selectVisualisation',
        values: [
          {
            label: 'Position Table',
            value: 'table',
          },
          {
            label: 'Exposure Graphics',
            value: 'exposure',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'table',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent(
          'selectVisualisation',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, []);

  const selectedPage = useTitleBarSelect('selectVisualisation');
  const SelectedComponent =
    selectedPage === 'exposure' ? ExposureGraphics : EquityTable;

  const { data, isLoading, isFetching, isError, error } = useAssetClassReportPageModel<EquityServerData>({
    fundId,
    reportKey: 'equity_specific_data',
    positionDate,
    isReportData: isEquityServerData,
  });

  return (
    <QueryBoundary<EquityServerData>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={data?.data}
      noDataMessage="No In-Scope or Available Data"
      errorMessage="No In-Scope or Available Data"
    >
      {(report) => (
        <SelectedComponent
          selectedEquityPage={selectedPage || 'table'}
          equityData={report}
          isFetching={isFetching}
        />
      )}
    </QueryBoundary>
  );
};

export default Equity;
